import { HeaderNavigationUserNavigationLinks } from '@/types';
import { useContext, useState } from 'react';
import { UserContext } from '../ContextProviders';
import { useTranslations } from 'next-intl';
import Link from '../general/Link';
import UserLight from '@/assets/user-light.svg';
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  useHover,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  safePolygon,
} from '@floating-ui/react';
import { motion } from 'framer-motion';
import classNames from 'classnames';

interface UserNavigationModel {
  links: HeaderNavigationUserNavigationLinks[];
  notificationsCount: number;
  dark?: boolean;
}

const UserNavigation = ({ notificationsCount, links, dark }: UserNavigationModel) => {
  const user = useContext(UserContext);
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState(false);

  const onOpenChange = (_open: boolean) => {
    if (_open) {
      setOpen(_open);
    } else {
      setTimeout(() => {
        setOpen(_open);
      }, 500);
    }
    setShow(_open);
  };

  const {
    refs,
    context,
    floatingStyles,
    placement: computedPlacement,
  } = useFloating({
    open,
    onOpenChange: onOpenChange,
    placement: 'bottom-end',
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
  });

  // Open/close on hover, stay open when moving pointer between button and menu
  const hover = useHover(context, {
    move: true,
    delay: { open: 50, close: 100 },
    handleClose: safePolygon(),
  });

  // Dismiss on outside click or Esc
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'menu' });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss, role]);

  const translate = {
    top: { translateY: 5 },
    bottom: { translateY: -5 },
  }[computedPlacement.includes('-') ? computedPlacement.split('-')[0] : computedPlacement];

  return user && user.isIpBasedIdentity ? (
    <Link link={{ href: '/open-login' }} className="btn">
      {t('general.$login')}
    </Link>
  ) : (
    <div className="relative flex h-full w-9 items-center justify-center">
      <button
        ref={refs.setReference}
        {...getReferenceProps({
          type: 'button',
          className: 'inline-flex h-9 w-9 items-center justify-center',
          'aria-label': 'Open menu',
          'aria-expanded': open,
        })}
      >
        <UserLight className={classNames('h-6 w-6', dark && 'fill-white')} />

        {notificationsCount > 0 && (
          <div className="bg-success absolute top-0 -right-2 flex min-w-5 items-center justify-center rounded-[10px] px-1 py-0.5 text-xs text-white">
            {notificationsCount}
          </div>
        )}
      </button>
      <FloatingPortal>
        {open && (
          <div ref={refs.setFloating} style={floatingStyles} {...getFloatingProps({ className: 'z-50' })}>
            <motion.div
              className={classNames(
                'content left-0 w-fit origin-top transform rounded-lg bg-white focus:outline-none',
                'flex max-h-96 flex-col overflow-hidden',
              )}
              style={{
                boxShadow:
                  '0 5px 5px -3px rgba(0, 0, 0, .2), 0 8px 10px 1px rgba(0, 0, 0, .14), 0 3px 14px 2px rgba(0, 0, 0, .12)',
              }}
              data-lenis-prevent
              tabIndex={-1}
              variants={{
                closed: {
                  opacity: 0,
                  ...translate,
                },
                open: {
                  opacity: 1,
                  translateX: 0,
                  translateY: 0,
                  transition: {
                    duration: 0.4,
                  },
                },
              }}
              initial="closed"
              exit="closed"
              animate={show ? 'open' : 'closed'}
            >
              <div className="flex flex-col">
                {links.map((link, index) => (
                  <Link
                    link={link.props.link}
                    key={index}
                    className="px-4 py-4 text-lg leading-5 transition-colors hover:bg-gray-100 focus:bg-gray-100"
                    onClick={() => onOpenChange(false)}
                  >
                    <span className="relative">
                      {link.props.badgeName === 'notification' && notificationsCount > 0 && (
                        <div className="bg-success absolute top-0.5 -right-6 flex min-w-5 items-center justify-center rounded-[10px] px-1 py-0.5 text-xs text-white">
                          {notificationsCount}
                        </div>
                      )}
                      {link.props.link.text}
                    </span>
                  </Link>
                ))}
              </div>
            </motion.div>
          </div>
        )}
      </FloatingPortal>
    </div>
  );
};

export default UserNavigation;
